// SPDX-License-Identifier: Apache-2.0

// Pure controls logic for the embodied rig — extracted from EmbodiedRig.tsx
// so the movement math is unit-testable without jsdom + PointerLock + r3f.
//
// Responsibilities:
//   - Hold a mutable KeyState map (the rig component flips bits on keydown/up).
//   - applyMovement(camera, dt, keys, opts) — yaw-only WASD, vertical Space/Shift.
//   - applyMouseLook(camera, dx, dy, sensitivity) — pitch clamped to ±π/2 - ε.
//
// The yaw-only forward vector is the gift-fix: when you tilt the camera up to
// look at the dome ceiling, WASD must NOT drift you upward — strafing has to
// stay parallel to the floor. Mirrored from v1 embodied-rig.tsx:107–113.

import * as THREE from "three";

export interface KeyState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
}

export function makeKeyState(): KeyState {
  return {
    forward: false,
    back: false,
    left: false,
    right: false,
    up: false,
    down: false,
  };
}

export function resetKeyState(keys: KeyState): void {
  keys.forward = false;
  keys.back = false;
  keys.left = false;
  keys.right = false;
  keys.up = false;
  keys.down = false;
}

/**
 * Map a KeyboardEvent.key to the KeyState slot it controls, or null if the
 * key is not bound. Lower-cases internally so callers don't have to.
 */
export function keyToSlot(rawKey: string): keyof KeyState | null {
  const k = rawKey.toLowerCase();
  if (k === "w") return "forward";
  if (k === "s") return "back";
  if (k === "a") return "left";
  if (k === "d") return "right";
  if (k === " " || k === "spacebar") return "up";
  if (k === "shift") return "down";
  return null;
}

/**
 * True if the event target is a text-entry surface — we must NOT capture
 * WASD while the user is typing into a chat box / wikilink autocomplete /
 * markdown editor. Mirrors v1 lines 69–70.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  if (el.isContentEditable) return true;
  return false;
}

export interface ApplyMovementOpts {
  /** Units per second. */
  speed: number;
  /** Reusable scratch vectors to avoid per-frame allocations. */
  scratch?: { fwd: THREE.Vector3; right: THREE.Vector3 };
}

const DEFAULT_FWD = new THREE.Vector3();
const DEFAULT_RIGHT = new THREE.Vector3();

/**
 * Move `camera` for `dt` seconds based on which keys are held.
 *   - WASD strafes along the camera's yaw-only forward/right vectors.
 *   - Space raises Y, Shift lowers Y (world-space, never relative to pitch).
 *
 * Returns the camera so it can be chained in tests.
 */
export function applyMovement(
  camera: THREE.PerspectiveCamera,
  dt: number,
  keys: KeyState,
  opts: ApplyMovementOpts,
): THREE.PerspectiveCamera {
  const v = opts.speed * dt;
  if (v === 0) return camera;

  const fwd = opts.scratch?.fwd ?? DEFAULT_FWD;
  const right = opts.scratch?.right ?? DEFAULT_RIGHT;

  // Yaw-only forward: take the camera's world-direction, zero Y, renormalise.
  // Fallback to -Z when the camera is staring straight up/down (length≈0).
  camera.getWorldDirection(fwd);
  fwd.y = 0;
  if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
  fwd.normalize();

  // Right = forward × up. camera.up is world-up by default (0,1,0).
  right.crossVectors(fwd, camera.up).normalize();

  if (keys.forward) camera.position.addScaledVector(fwd, v);
  if (keys.back) camera.position.addScaledVector(fwd, -v);
  if (keys.right) camera.position.addScaledVector(right, v);
  if (keys.left) camera.position.addScaledVector(right, -v);
  if (keys.up) camera.position.y += v;
  if (keys.down) camera.position.y -= v;

  return camera;
}

const PITCH_LIMIT = Math.PI / 2 - 1e-3;

/**
 * Apply mouse look — yaw rotates around world-Y, pitch rotates around the
 * camera's local X. Pitch clamped so you can't flip upside-down (the
 * vault-inside-your-brain aesthetic falls apart if the floor flips).
 *
 * `dx` and `dy` are in raw movementX / movementY pixels (positive dx = mouse
 * moved right; positive dy = mouse moved down). We negate them so right-mouse
 * yaws right and down-mouse looks down.
 */
export function applyMouseLook(
  camera: THREE.PerspectiveCamera,
  dx: number,
  dy: number,
  sensitivity: number,
): void {
  // Decompose current orientation → YXZ Euler so we can clamp pitch and
  // recompose without accumulating roll drift.
  const e = new THREE.Euler().setFromQuaternion(camera.quaternion, "YXZ");
  e.y -= dx * sensitivity;
  e.x -= dy * sensitivity;
  if (e.x > PITCH_LIMIT) e.x = PITCH_LIMIT;
  if (e.x < -PITCH_LIMIT) e.x = -PITCH_LIMIT;
  e.z = 0;
  camera.quaternion.setFromEuler(e);
}
